import { useEditor, EditorContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Youtube from '@tiptap/extension-youtube';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import { Node, mergeAttributes } from '@tiptap/core';
import { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Code,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Undo,
  Redo,
  Link as LinkIcon,
  Image as ImageIcon,
  Youtube as YoutubeIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Highlighter,
  FileCode,
  Presentation,
  Lock,
  Video,
  FileText,
  FileUp,
  Loader2,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  MoreVertical,
  X
} from 'lucide-react';
import { toast } from 'sonner';

// Script to inject in iframe srcdoc to handle anchor links properly
const iframeNavScript = `<script data-nav-handler="true">
  document.addEventListener('click', function(e) {
    var link = e.target.closest('a');
    if (!link) return;
    var href = link.getAttribute('href');
    if (!href) return;
    
    if (href.startsWith('#')) {
      e.preventDefault();
      e.stopPropagation();
      var targetId = href.slice(1);
      var target = document.getElementById(targetId) || document.querySelector('[name="' + targetId + '"]');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return false;
    }
    
    if (href.startsWith('http://') || href.startsWith('https://')) {
      e.preventDefault();
      window.open(href, '_blank', 'noopener,noreferrer');
      return false;
    }
  }, true);
<\/script>`;

// Helper to add navigation handler to HTML
const addBaseTargetToHtml = (html) => {
  if (!html) return html;
  
  let processed = html;
  
  // Add navigation handler script if not present
  if (!processed.includes('data-nav-handler')) {
    if (processed.includes('</body>')) {
      processed = processed.replace('</body>', iframeNavScript + '</body>');
    } else if (processed.includes('</html>')) {
      processed = processed.replace('</html>', iframeNavScript + '</html>');
    } else {
      processed = processed + iframeNavScript;
    }
  }
  
  // Add base target if not present
  if (!processed.includes('<base')) {
    if (processed.includes('<head>')) {
      processed = processed.replace('<head>', '<head><base target="_self">');
    } else if (processed.includes('<head ')) {
      processed = processed.replace(/<head([^>]*)>/, '<head$1><base target="_self">');
    } else if (processed.includes('<html')) {
      processed = processed.replace(/<html([^>]*)>/i, '<html$1><head><base target="_self"></head>');
    } else if (processed.includes('<!DOCTYPE')) {
      processed = processed.replace(/(<!DOCTYPE[^>]*>)/i, '$1<head><base target="_self"></head>');
    }
  }
  
  return processed;
};

// NodeView Component for HtmlBlock - allows editing
const HtmlBlockView = ({ node, updateAttributes, deleteNode, selected }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(node.attrs.content);
  const [editHeight, setEditHeight] = useState(node.attrs.height);
  const [showPreview, setShowPreview] = useState(true);

  const handleSave = () => {
    updateAttributes({ content: editContent, height: editHeight });
    setIsEditing(false);
    toast.success('Bloc HTML mis à jour');
  };

  const handleDelete = () => {
    if (confirm('Supprimer ce bloc HTML ?')) {
      deleteNode();
    }
  };

  return (
    <NodeViewWrapper className={`my-4 ${selected ? 'ring-2 ring-purple-500' : ''}`}>
      <div className="relative group">
        {/* Toolbar */}
        <div className="absolute top-2 right-2 z-10 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button size="sm" variant="secondary" onClick={() => setShowPreview(!showPreview)} title={showPreview ? 'Masquer' : 'Afficher'}>
            {showPreview ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setIsEditing(true)} title="Modifier">
            <Pencil className="h-3 w-3" />
          </Button>
          <Button size="sm" variant="destructive" onClick={handleDelete} title="Supprimer">
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>

        {/* Label */}
        <div className="absolute top-2 left-2 z-10 bg-purple-600 text-white text-xs px-2 py-1 rounded">
          Bloc HTML
        </div>

        {/* Preview */}
        {showPreview ? (
          <div className="border rounded-lg overflow-hidden">
            <iframe
              srcDoc={addBaseTargetToHtml(node.attrs.content)}
              style={{ width: '100%', height: `${node.attrs.height}px`, border: 'none' }}
              sandbox="allow-scripts allow-same-origin"
            />
          </div>
        ) : (
          <div className="border rounded-lg p-4 bg-muted text-center text-muted-foreground">
            <FileCode className="h-8 w-8 mx-auto mb-2" />
            Bloc HTML (masqué) - {Math.round((node.attrs.content?.length || 0) / 1024)}Ko
          </div>
        )}

        {/* Edit Dialog */}
        {isEditing && (
          <Dialog open={isEditing} onOpenChange={setIsEditing}>
            <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle>Modifier le bloc HTML</DialogTitle>
              </DialogHeader>
              <div className="flex-1 space-y-4 overflow-auto">
                <div className="space-y-2">
                  <Label>Code HTML</Label>
                  <Textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={15}
                    className="font-mono text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Hauteur de l'iframe (px)</Label>
                  <Input
                    type="number"
                    value={editHeight}
                    onChange={(e) => setEditHeight(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setIsEditing(false)}>Annuler</Button>
                <Button onClick={handleSave}>Enregistrer</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </NodeViewWrapper>
  );
};

// Custom extension for iframes (Google Slides, etc.)
const Iframe = Node.create({
  name: 'iframe',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      src: { default: null },
      width: { default: '100%' },
      height: { default: '450' },
      frameborder: { default: '0' },
      allowfullscreen: { default: 'true' },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'iframe',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      { class: 'my-4 aspect-video w-full rounded-lg overflow-hidden border' },
      [
        'iframe',
        mergeAttributes(HTMLAttributes, {
          style: 'width:100%;height:100%;',
          allowfullscreen: 'true',
          mozallowfullscreen: 'true',
          webkitallowfullscreen: 'true',
        }),
      ],
    ];
  },

  addCommands() {
    return {
      setIframe: (options) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: options,
        });
      },
    };
  },
});

// Custom extension for Google Slides iframe with restrictions
const GoogleSlides = Node.create({
  name: 'googleSlides',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      src: { default: null },
      disableDownload: { default: false },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-google-slides]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    // Don't block pointer events - users need to interact with slides navigation
    return [
      'div',
      { 'data-google-slides': '', class: 'my-4 relative' },
      [
        'div',
        { class: 'aspect-video w-full rounded-lg overflow-hidden border' },
        [
          'iframe',
          mergeAttributes({
            src: HTMLAttributes.src,
            frameborder: '0',
            allowfullscreen: 'true',
            mozallowfullscreen: 'true',
            webkitallowfullscreen: 'true',
            style: 'width:100%;height:100%;',
          }),
        ],
      ],
      ...(HTMLAttributes.disableDownload ? [
        ['div', { class: 'absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded pointer-events-none' }, 'Téléchargement désactivé']
      ] : []),
    ];
  },

  addCommands() {
    return {
      setGoogleSlides: (options) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: options,
        });
      },
    };
  },
});

// Custom extension for Google Drive Video
const GoogleDriveVideo = Node.create({
  name: 'googleDriveVideo',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      src: { default: null },
      disableDownload: { default: true },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-drive-video]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      { 'data-drive-video': '', class: 'my-4 relative' },
      [
        'div',
        { class: 'aspect-video w-full rounded-lg overflow-hidden border bg-black' },
        [
          'iframe',
          mergeAttributes({
            src: HTMLAttributes.src,
            frameborder: '0',
            allowfullscreen: 'true',
            allow: 'autoplay',
            style: 'width:100%;height:100%;',
          }),
        ],
      ],
      ...(HTMLAttributes.disableDownload ? [
        ['div', { class: 'absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1' }, '🔒 Téléchargement désactivé']
      ] : []),
    ];
  },

  addCommands() {
    return {
      setGoogleDriveVideo: (options) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: options,
        });
      },
    };
  },
});

// Custom extension for full HTML content (rendered in iframe for security)
const HtmlBlock = Node.create({
  name: 'htmlBlock',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      content: { default: '' },
      height: { default: '800' },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-html-block]',
        getAttrs: (node) => {
          if (typeof node === 'string') {
            return { content: '', height: '800' };
          }
          
          const iframe = node.querySelector('iframe');
          if (iframe) {
            // Get content from srcdoc
            let content = iframe.getAttribute('srcdoc') || '';
            
            // Decode HTML entities if needed
            if (content) {
              const textArea = document.createElement('textarea');
              textArea.innerHTML = content;
              content = textArea.value;
            }
            
            // Get height from style
            const style = iframe.getAttribute('style') || '';
            const heightMatch = style.match(/height:\s*(\d+)px/);
            const height = heightMatch ? heightMatch[1] : '800';
            
            console.log('[HtmlBlock parseHTML] Parsed content length:', content.length, 'height:', height);
            
            return { content, height };
          }
          
          console.log('[HtmlBlock parseHTML] No iframe found in node');
          return { content: '', height: '800' };
        },
      },
      // Also parse iframes with srcdoc directly inside div.my-4 (legacy format)
      {
        tag: 'div.my-4',
        getAttrs: (node) => {
          if (typeof node === 'string') return false;
          
          // Only match if there's an iframe with srcdoc and no other specific markers
          const iframe = node.querySelector(':scope > iframe[srcdoc]');
          if (!iframe) return false;
          
          // Skip if it has other specific markers
          if (node.hasAttribute('data-google-slides') || node.hasAttribute('data-drive-video')) {
            return false;
          }
          
          let content = iframe.getAttribute('srcdoc') || '';
          if (content) {
            const textArea = document.createElement('textarea');
            textArea.innerHTML = content;
            content = textArea.value;
          }
          
          const style = iframe.getAttribute('style') || '';
          const heightMatch = style.match(/height:\s*(\d+)px/);
          const height = heightMatch ? heightMatch[1] : '800';
          
          console.log('[HtmlBlock parseHTML legacy] Parsed content length:', content.length);
          
          return { content, height };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    // Encode the HTML content for the srcdoc attribute
    let encodedContent = HTMLAttributes.content;
    
    // Ensure links stay within iframe by adding base target
    if (encodedContent && !encodedContent.includes('<base')) {
      if (encodedContent.includes('<head>')) {
        encodedContent = encodedContent.replace('<head>', '<head><base target="_self">');
      } else if (encodedContent.includes('<head ')) {
        encodedContent = encodedContent.replace(/<head([^>]*)>/, '<head$1><base target="_self">');
      } else if (encodedContent.includes('<html')) {
        encodedContent = encodedContent.replace(/<html([^>]*)>/i, '<html$1><head><base target="_self"></head>');
      } else if (encodedContent.includes('<!DOCTYPE')) {
        // Add after DOCTYPE
        encodedContent = encodedContent.replace(/(<!DOCTYPE[^>]*>)/i, '$1<head><base target="_self"></head>');
      }
    }
    
    return [
      'div',
      { 'data-html-block': '', class: 'my-4 border rounded-lg overflow-hidden' },
      [
        'iframe',
        {
          srcdoc: encodedContent,
          style: `width:100%;height:${HTMLAttributes.height}px;border:none;`,
          sandbox: 'allow-scripts allow-same-origin',
        },
      ],
    ];
  },

  addCommands() {
    return {
      setHtmlBlock: (options) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: options,
        });
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(HtmlBlockView);
  },
});

// ========================== PDF SLIDES ==========================

// NodeView Component for PDF Slides viewer
const PdfSlidesView = ({ node, updateAttributes, deleteNode, selected }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const fsContainerRef = useCallback(node => {
    if (node) fsRef.current = node;
  }, []);
  const fsRef = { current: null };
  const slidesElRef = useState(null);
  
  const images = node.attrs.images || [];
  const totalPages = images.length;
  const filename = node.attrs.filename || 'PDF';
  
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  const goToPage = (page) => {
    if (page >= 0 && page < totalPages) {
      setCurrentPage(page);
    }
  };
  
  const toggleFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        // Find the slides container div
        const el = document.getElementById(`pdf-slides-${node.attrs.filename}-${totalPages}`);
        if (el) {
          await el.requestFullscreen();
        }
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
    }
  }, [node.attrs.filename, totalPages]);
  
  const handleDelete = () => {
    if (confirm('Supprimer ce PDF Slides ?')) {
      deleteNode();
    }
  };
  
  // Listen for native fullscreen change events
  useEffect(() => {
    const handleFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);
  
  // Handle keyboard navigation in fullscreen
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); goToPage(currentPage - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goToPage(currentPage + 1); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isFullscreen, currentPage, totalPages]);
  
  if (totalPages === 0) {
    return (
      <NodeViewWrapper className="my-4">
        <div className="border rounded-lg p-8 text-center text-muted-foreground bg-muted/30">
          <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p>PDF vide</p>
        </div>
      </NodeViewWrapper>
    );
  }
  
  const slidesId = `pdf-slides-${node.attrs.filename}-${totalPages}`;
  
  return (
    <NodeViewWrapper className={`my-4 ${selected && !isFullscreen ? 'ring-2 ring-primary' : ''}`} contentEditable={false}>
      <div 
        id={slidesId}
        className={`relative ${isFullscreen ? 'bg-black flex flex-col h-full w-full' : ''}`}
      >
        {/* Slide container */}
        <div className={`relative ${isFullscreen ? 'flex-1 flex items-center justify-center overflow-hidden' : 'rounded-t-lg overflow-hidden border border-b-0'}`}>
          {/* PDF Import label (top right, edit mode only) */}
          {!isFullscreen && (
            <div className="absolute top-3 right-3 z-10 bg-black/60 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-md">
              PDF Import
            </div>
          )}
          
          {/* Save badge (top left, edit mode only)  */}
          {!isFullscreen && selected && (
            <button
              className="absolute top-3 left-3 z-10 flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md text-white"
              style={{ background: 'linear-gradient(135deg, #e53e3e, #c53030)' }}
              contentEditable={false}
            >
              <span style={{ fontSize: 14 }}>⊙</span> Enregistrer
            </button>
          )}
          
          {/* The slide image */}
          <img
            src={`${API_URL}${images[currentPage]}`}
            alt={`${filename} - Page ${currentPage + 1}`}
            className={isFullscreen 
              ? 'max-h-full max-w-full object-contain' 
              : 'w-full block'
            }
            style={!isFullscreen ? { minHeight: 300, background: '#0d1117' } : {}}
            draggable={false}
          />
        </div>
        
        {/* Bottom navigation bar */}
        <div className={`flex items-center justify-between px-3 py-2 ${
          isFullscreen 
            ? 'bg-gray-900/90 backdrop-blur-sm border-t border-gray-700' 
            : 'bg-card border border-t-0 rounded-b-lg'
        }`} contentEditable={false}>
          {/* Left: page navigation */}
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); goToPage(currentPage - 1); }}
              disabled={currentPage === 0}
              className={`p-1.5 rounded-md transition-colors ${
                currentPage === 0 
                  ? 'opacity-30 cursor-not-allowed' 
                  : isFullscreen ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-muted text-muted-foreground'
              }`}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            
            <span className={`text-sm font-medium min-w-[2ch] text-center tabular-nums ${
              isFullscreen ? 'text-gray-200' : ''
            }`}>
              {currentPage + 1}
            </span>
            
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); goToPage(currentPage + 1); }}
              disabled={currentPage === totalPages - 1}
              className={`p-1.5 rounded-md transition-colors ${
                currentPage === totalPages - 1 
                  ? 'opacity-30 cursor-not-allowed' 
                  : isFullscreen ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-muted text-muted-foreground'
              }`}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            
            {/* Three-dot menu */}
            <div className="relative ml-1">
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen(!menuOpen); }}
                className={`p-1.5 rounded-md transition-colors ${
                  isFullscreen ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-muted text-muted-foreground'
                }`}
              >
                <MoreVertical className="h-4 w-4" />
              </button>
              
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-50" onClick={() => setMenuOpen(false)} />
                  <div className={`absolute bottom-full left-0 mb-2 z-50 min-w-[220px] rounded-lg shadow-xl border py-1.5 ${
                    isFullscreen ? 'bg-gray-800 border-gray-600' : 'bg-popover border-border'
                  }`}>
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFullscreen(); setMenuOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2.5 transition-colors ${
                        isFullscreen ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-muted'
                      }`}
                    >
                      {isFullscreen ? <X className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                      {isFullscreen ? 'Quitter le plein écran' : 'Activer le mode plein écran'}
                    </button>
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); goToPage(0); setMenuOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2.5 transition-colors ${
                        isFullscreen ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-muted'
                      }`}
                    >
                      <FileText className="h-4 w-4" />
                      Revenir à la première page
                    </button>
                    <div className={`my-1 ${isFullscreen ? 'border-t border-gray-600' : 'border-t'}`} />
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDelete(); setMenuOpen(false); }}
                      className="w-full text-left px-3 py-2 text-sm flex items-center gap-2.5 text-red-500 hover:bg-red-500/10 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                      Supprimer
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
          
          {/* Right: page counter + fullscreen + label */}
          <div className="flex items-center gap-3">
            <span className={`text-sm tabular-nums ${
              isFullscreen ? 'text-gray-400' : 'text-muted-foreground'
            }`}>
              {currentPage + 1} / {totalPages}
            </span>
            
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFullscreen(); }}
              className={`p-1.5 rounded-md transition-colors ${
                isFullscreen ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-muted text-muted-foreground'
              }`}
              title={isFullscreen ? 'Quitter le plein écran' : 'Plein écran'}
            >
              {isFullscreen ? <X className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            
            <span className={`text-xs font-medium flex items-center gap-1.5 ${
              isFullscreen ? 'text-gray-400' : 'text-muted-foreground'
            }`}>
              <FileText className="h-3.5 w-3.5" />
              PDF Slides
            </span>
          </div>
        </div>
      </div>
    </NodeViewWrapper>
  );
};

// TipTap extension for PDF Slides
const PdfSlides = Node.create({
  name: 'pdfSlides',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      images: { default: [] },
      filename: { default: '' },
      totalPages: { default: 0 },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-pdf-slides]',
        getAttrs: (node) => {
          if (typeof node === 'string') return { images: [], filename: '', totalPages: 0 };
          try {
            const images = JSON.parse(node.getAttribute('data-images') || '[]');
            return {
              images,
              filename: node.getAttribute('data-filename') || '',
              totalPages: parseInt(node.getAttribute('data-total-pages') || '0'),
            };
          } catch {
            return { images: [], filename: '', totalPages: 0 };
          }
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      {
        'data-pdf-slides': '',
        'data-images': JSON.stringify(HTMLAttributes.images || []),
        'data-filename': HTMLAttributes.filename || '',
        'data-total-pages': String(HTMLAttributes.totalPages || 0),
        class: 'pdf-slides-container',
      },
    ];
  },

  addCommands() {
    return {
      setPdfSlides: (options) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: options,
        });
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(PdfSlidesView);
  },
});

const MenuButton = ({ onClick, isActive, disabled, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-2 rounded hover:bg-muted transition-colors ${
      isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    {children}
  </button>
);

const Divider = () => <div className="w-px h-6 bg-border mx-1" />;

export const RichTextEditor = ({ content, onChange, placeholder = "Commencez à écrire..." }) => {
  const [linkUrl, setLinkUrl] = useState('');
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [youtubeDialogOpen, setYoutubeDialogOpen] = useState(false);
  const [htmlDialogOpen, setHtmlDialogOpen] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const [htmlHeight, setHtmlHeight] = useState('900');
  const [driveDialogOpen, setDriveDialogOpen] = useState(false);
  const [driveUrl, setDriveUrl] = useState('');
  const [driveRestrictions, setDriveRestrictions] = useState({
    disableDownload: true,
    disablePrint: true,
    disableCopy: true,
  });
  // Google Drive Video
  const [driveVideoDialogOpen, setDriveVideoDialogOpen] = useState(false);
  const [driveVideoUrl, setDriveVideoUrl] = useState('');
  const [driveVideoDownloadDisabled, setDriveVideoDownloadDisabled] = useState(true);
  
  // PDF Import
  const [pdfImporting, setPdfImporting] = useState(false);
  const pdfInputRef = useState(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg my-4',
        },
      }),
      Youtube.configure({
        width: 640,
        height: 360,
        HTMLAttributes: {
          class: 'w-full aspect-video rounded-lg my-4',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      Highlight.configure({
        multicolor: true,
      }),
      Iframe,
      GoogleSlides,
      GoogleDriveVideo,
      HtmlBlock,
      PdfSlides,
    ],
    content: content || '',
    editorProps: {
      attributes: {
        class: 'prose prose-slate max-w-none min-h-[400px] p-4 focus:outline-none',
      },
    },
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
  });

  const setLink = useCallback(() => {
    if (!linkUrl) {
      editor?.chain().focus().unsetLink().run();
      return;
    }
    editor?.chain().focus().setLink({ href: linkUrl }).run();
    setLinkUrl('');
    setLinkDialogOpen(false);
  }, [editor, linkUrl]);

  const addImage = useCallback(() => {
    if (imageUrl) {
      editor?.chain().focus().setImage({ src: imageUrl }).run();
      setImageUrl('');
      setImageDialogOpen(false);
    }
  }, [editor, imageUrl]);

  const addYoutube = useCallback(() => {
    if (youtubeUrl) {
      editor?.chain().focus().setYoutubeVideo({ src: youtubeUrl }).run();
      setYoutubeUrl('');
      setYoutubeDialogOpen(false);
    }
  }, [editor, youtubeUrl]);

  const insertHtmlBlock = useCallback(() => {
    if (htmlContent) {
      // Check if it's an iframe and extract src
      const iframeMatch = htmlContent.match(/<iframe[^>]*src=["']([^"']+)["'][^>]*>/i);
      if (iframeMatch) {
        const src = iframeMatch[1];
        editor?.chain().focus().setIframe({ src }).run();
      } else {
        editor?.chain().focus().insertContent(htmlContent).run();
      }
      setHtmlContent('');
      setHtmlDialogOpen(false);
    }
  }, [editor, htmlContent]);

  // Import PDF function - converts to slide images
  const handlePdfImport = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Veuillez sélectionner un fichier PDF');
      return;
    }
    
    setPdfImporting(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/extract-pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de l\'import');
      }
      
      const data = await response.json();
      
      if (data.mode === 'slides' && data.images?.length > 0) {
        // Insert as PDF Slides viewer
        editor?.chain().focus().setPdfSlides({
          images: data.images,
          filename: data.filename || file.name,
          totalPages: data.pages || data.images.length,
        }).run();
        toast.success(`PDF importé avec succès — ${data.pages} page${data.pages > 1 ? 's' : ''} (${file.name})`);
      } else if (data.html) {
        // Fallback: insert as HTML
        editor?.chain().focus().insertContent(data.html).run();
        toast.success(`PDF importé avec succès (${file.name})`);
      }
    } catch (error) {
      console.error('PDF import error:', error);
      toast.error(error.message || 'Erreur lors de l\'import du PDF');
    } finally {
      setPdfImporting(false);
      // Reset the input
      event.target.value = '';
    }
  }, [editor]);

  const insertGoogleSlide = useCallback(() => {
    if (driveUrl) {
      let fileId = '';
      const patterns = [
        /\/d\/([a-zA-Z0-9_-]+)/,
        /id=([a-zA-Z0-9_-]+)/,
        /\/presentation\/d\/([a-zA-Z0-9_-]+)/,
      ];
      
      for (const pattern of patterns) {
        const match = driveUrl.match(pattern);
        if (match) {
          fileId = match[1];
          break;
        }
      }

      if (fileId) {
        const embedUrl = `https://docs.google.com/presentation/d/${fileId}/embed?start=false&loop=false&delayms=3000`;
        
        // Use the custom GoogleSlides extension
        editor?.chain().focus().setGoogleSlides({
          src: embedUrl,
          disableDownload: driveRestrictions.disableDownload,
        }).run();
      }
      
      setDriveUrl('');
      setDriveDialogOpen(false);
    }
  }, [editor, driveUrl, driveRestrictions]);

  // Insert Google Drive Video
  const insertGoogleDriveVideo = useCallback(() => {
    if (driveVideoUrl) {
      let fileId = '';
      const patterns = [
        /\/d\/([a-zA-Z0-9_-]+)/,
        /id=([a-zA-Z0-9_-]+)/,
        /\/file\/d\/([a-zA-Z0-9_-]+)/,
      ];
      
      for (const pattern of patterns) {
        const match = driveVideoUrl.match(pattern);
        if (match) {
          fileId = match[1];
          break;
        }
      }

      if (fileId) {
        // Google Drive video preview URL
        const embedUrl = `https://drive.google.com/file/d/${fileId}/preview`;
        
        editor?.chain().focus().setGoogleDriveVideo({
          src: embedUrl,
          disableDownload: driveVideoDownloadDisabled,
        }).run();
      }
      
      setDriveVideoUrl('');
      setDriveVideoDialogOpen(false);
    }
  }, [editor, driveVideoUrl, driveVideoDownloadDisabled]);

  // Insert full HTML block (rendered in iframe)
  const insertFullHtmlBlock = useCallback(() => {
    if (htmlContent) {
      // Check if it's a full HTML document (with DOCTYPE or html tag)
      const isFullHtml = htmlContent.includes('<!DOCTYPE') || htmlContent.includes('<html');
      
      if (isFullHtml) {
        // Add base target to keep all links within the iframe
        let processedHtml = htmlContent;
        
        // If HTML has a <head> section, add <base target="_self"> inside it
        if (processedHtml.includes('<head>') && !processedHtml.includes('<base')) {
          processedHtml = processedHtml.replace('<head>', '<head><base target="_self">');
        } else if (processedHtml.includes('<head ') && !processedHtml.includes('<base')) {
          // Handle <head with attributes
          processedHtml = processedHtml.replace(/<head([^>]*)>/, '<head$1><base target="_self">');
        } else if (!processedHtml.includes('<head') && processedHtml.includes('<html')) {
          // No head section, add one after <html>
          processedHtml = processedHtml.replace(/<html([^>]*)>/, '<html$1><head><base target="_self"></head>');
        }
        
        // Insert as HtmlBlock (rendered in iframe)
        editor?.chain().focus().setHtmlBlock({
          content: processedHtml,
          height: htmlHeight,
        }).run();
      } else {
        // Check if it's an iframe and extract src
        const iframeMatch = htmlContent.match(/<iframe[^>]*src=["']([^"']+)["'][^>]*>/i);
        if (iframeMatch) {
          const src = iframeMatch[1];
          editor?.chain().focus().setIframe({ src }).run();
        } else {
          // Insert as regular HTML
          editor?.chain().focus().insertContent(htmlContent).run();
        }
      }
      setHtmlContent('');
      setHtmlDialogOpen(false);
    }
  }, [editor, htmlContent, htmlHeight]);

  if (!editor) {
    return <div className="h-[400px] rounded-lg bg-muted animate-pulse" />;
  }

  return (
    <div className="border rounded-lg overflow-hidden bg-card">
      {/* Toolbar */}
      <div className="border-b bg-muted/30 p-2 flex flex-wrap gap-1 items-center">
        <MenuButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Annuler">
          <Undo className="h-4 w-4" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Refaire">
          <Redo className="h-4 w-4" />
        </MenuButton>

        <Divider />

        <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} isActive={editor.isActive('heading', { level: 1 })} title="Titre 1">
          <Heading1 className="h-4 w-4" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} isActive={editor.isActive('heading', { level: 2 })} title="Titre 2">
          <Heading2 className="h-4 w-4" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} isActive={editor.isActive('heading', { level: 3 })} title="Titre 3">
          <Heading3 className="h-4 w-4" />
        </MenuButton>

        <Divider />

        <MenuButton onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} title="Gras">
          <Bold className="h-4 w-4" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} title="Italique">
          <Italic className="h-4 w-4" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleUnderline().run()} isActive={editor.isActive('underline')} title="Souligné">
          <UnderlineIcon className="h-4 w-4" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleStrike().run()} isActive={editor.isActive('strike')} title="Barré">
          <Strikethrough className="h-4 w-4" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleHighlight().run()} isActive={editor.isActive('highlight')} title="Surligné">
          <Highlighter className="h-4 w-4" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleCode().run()} isActive={editor.isActive('code')} title="Code">
          <Code className="h-4 w-4" />
        </MenuButton>

        <Divider />

        <MenuButton onClick={() => editor.chain().focus().setTextAlign('left').run()} isActive={editor.isActive({ textAlign: 'left' })} title="Gauche">
          <AlignLeft className="h-4 w-4" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().setTextAlign('center').run()} isActive={editor.isActive({ textAlign: 'center' })} title="Centre">
          <AlignCenter className="h-4 w-4" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().setTextAlign('right').run()} isActive={editor.isActive({ textAlign: 'right' })} title="Droite">
          <AlignRight className="h-4 w-4" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().setTextAlign('justify').run()} isActive={editor.isActive({ textAlign: 'justify' })} title="Justifié">
          <AlignJustify className="h-4 w-4" />
        </MenuButton>

        <Divider />

        <MenuButton onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive('bulletList')} title="Liste">
          <List className="h-4 w-4" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive('orderedList')} title="Liste numérotée">
          <ListOrdered className="h-4 w-4" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleBlockquote().run()} isActive={editor.isActive('blockquote')} title="Citation">
          <Quote className="h-4 w-4" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} isActive={editor.isActive('codeBlock')} title="Bloc de code">
          <FileCode className="h-4 w-4" />
        </MenuButton>

        <Divider />

        {/* Link Dialog */}
        <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
          <DialogTrigger asChild>
            <button type="button" className={`p-2 rounded hover:bg-muted transition-colors ${editor.isActive('link') ? 'bg-primary/10 text-primary' : 'text-muted-foreground'}`} title="Lien">
              <LinkIcon className="h-4 w-4" />
            </button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Insérer un lien</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>URL</Label>
                <Input placeholder="https://example.com" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>Annuler</Button>
                <Button onClick={setLink}>Insérer</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Image Dialog */}
        <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
          <DialogTrigger asChild>
            <button type="button" className="p-2 rounded hover:bg-muted transition-colors text-muted-foreground" title="Image">
              <ImageIcon className="h-4 w-4" />
            </button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Insérer une image</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>URL de l'image</Label>
                <Input placeholder="https://example.com/image.jpg" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setImageDialogOpen(false)}>Annuler</Button>
                <Button onClick={addImage}>Insérer</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* YouTube Dialog */}
        <Dialog open={youtubeDialogOpen} onOpenChange={setYoutubeDialogOpen}>
          <DialogTrigger asChild>
            <button type="button" className="p-2 rounded hover:bg-muted transition-colors text-muted-foreground" title="YouTube">
              <YoutubeIcon className="h-4 w-4" />
            </button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Insérer une vidéo YouTube</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>URL YouTube</Label>
                <Input placeholder="https://www.youtube.com/watch?v=..." value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setYoutubeDialogOpen(false)}>Annuler</Button>
                <Button onClick={addYoutube}>Insérer</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Google Slides Dialog */}
        <Dialog open={driveDialogOpen} onOpenChange={setDriveDialogOpen}>
          <DialogTrigger asChild>
            <button type="button" className="p-2 rounded hover:bg-muted transition-colors text-muted-foreground" title="Google Slides">
              <Presentation className="h-4 w-4" />
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Insérer Google Slides</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>URL Google Slides</Label>
                <Input placeholder="https://docs.google.com/presentation/d/..." value={driveUrl} onChange={(e) => setDriveUrl(e.target.value)} />
                <p className="text-xs text-muted-foreground">Collez l'URL de partage de votre présentation</p>
              </div>
              <div className="space-y-3 p-4 rounded-lg bg-muted/30 border">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Lock className="h-4 w-4" />
                  Restrictions
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-normal">Désactiver le téléchargement</Label>
                    <Switch checked={driveRestrictions.disableDownload} onCheckedChange={(c) => setDriveRestrictions({ ...driveRestrictions, disableDownload: c })} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-normal">Désactiver l'impression</Label>
                    <Switch checked={driveRestrictions.disablePrint} onCheckedChange={(c) => setDriveRestrictions({ ...driveRestrictions, disablePrint: c })} />
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDriveDialogOpen(false)}>Annuler</Button>
                <Button onClick={insertGoogleSlide}>Insérer</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Google Drive Video Dialog */}
        <Dialog open={driveVideoDialogOpen} onOpenChange={setDriveVideoDialogOpen}>
          <DialogTrigger asChild>
            <button type="button" className="p-2 rounded hover:bg-muted transition-colors text-muted-foreground" title="Vidéo Google Drive">
              <Video className="h-4 w-4" />
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Insérer une vidéo Google Drive</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>URL de la vidéo Google Drive</Label>
                <Input placeholder="https://drive.google.com/file/d/..." value={driveVideoUrl} onChange={(e) => setDriveVideoUrl(e.target.value)} />
                <p className="text-xs text-muted-foreground">Collez l'URL de partage de votre vidéo Google Drive</p>
              </div>
              <div className="space-y-3 p-4 rounded-lg bg-muted/30 border">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Lock className="h-4 w-4" />
                  Options
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-normal">Désactiver le téléchargement</Label>
                  <Switch checked={driveVideoDownloadDisabled} onCheckedChange={setDriveVideoDownloadDisabled} />
                </div>
                <p className="text-xs text-muted-foreground">
                  Note: Pour empêcher le téléchargement, assurez-vous aussi de désactiver cette option dans les paramètres de partage Google Drive.
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDriveVideoDialogOpen(false)}>Annuler</Button>
                <Button onClick={insertGoogleDriveVideo}>Insérer</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* HTML Block Dialog */}
        <Dialog open={htmlDialogOpen} onOpenChange={setHtmlDialogOpen}>
          <DialogTrigger asChild>
            <button type="button" className="p-2 rounded hover:bg-muted transition-colors text-muted-foreground" title="Bloc HTML / TP personnalisé">
              <FileText className="h-4 w-4" />
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader><DialogTitle>Bloc HTML personnalisé / TP</DialogTitle></DialogHeader>
            <Tabs defaultValue="code" className="flex-1 flex flex-col overflow-hidden">
              <TabsList>
                <TabsTrigger value="code">Code HTML</TabsTrigger>
                <TabsTrigger value="templates">Templates rapides</TabsTrigger>
              </TabsList>
              <TabsContent value="code" className="flex-1 space-y-4 overflow-auto">
                <div className="space-y-2">
                  <Label>Code HTML (supporte les pages complètes avec CSS/Bootstrap)</Label>
                  <Textarea 
                    placeholder="<!DOCTYPE html>
<html>
<head>
  <link href='https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css' rel='stylesheet'>
  <style>
    /* Vos styles CSS */
  </style>
</head>
<body>
  <!-- Votre contenu -->
</body>
</html>" 
                    value={htmlContent} 
                    onChange={(e) => setHtmlContent(e.target.value)} 
                    rows={12} 
                    className="font-mono text-sm" 
                  />
                  <p className="text-xs text-muted-foreground">
                    Vous pouvez coller un document HTML complet avec Bootstrap, CSS personnalisé, etc. Il sera rendu dans une iframe isolée.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Hauteur de l'iframe (px)</Label>
                  <Input 
                    type="number" 
                    value={htmlHeight} 
                    onChange={(e) => setHtmlHeight(e.target.value)} 
                    placeholder="500"
                  />
                </div>
              </TabsContent>
              <TabsContent value="templates" className="space-y-2 overflow-auto">
                <Button variant="outline" className="w-full justify-start" onClick={() => setHtmlContent(`<div class="p-4 bg-blue-100 border-l-4 border-blue-500 rounded"><strong>Info:</strong> Votre message</div>`)}>
                  📘 Boîte d'information (bleue)
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => setHtmlContent(`<div class="p-4 bg-yellow-100 border-l-4 border-yellow-500 rounded"><strong>Attention:</strong> Votre message</div>`)}>
                  ⚠️ Boîte d'avertissement (jaune)
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => setHtmlContent(`<div class="p-4 bg-red-100 border-l-4 border-red-500 rounded"><strong>Important:</strong> Votre message</div>`)}>
                  🔴 Boîte importante (rouge)
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => setHtmlContent(`<div class="p-4 bg-green-100 border-l-4 border-green-500 rounded"><strong>Succès:</strong> Votre message</div>`)}>
                  ✅ Boîte succès (verte)
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => setHtmlContent(`<details class="border rounded p-4"><summary class="font-bold cursor-pointer">Cliquez pour voir</summary><p class="mt-2">Contenu caché...</p></details>`)}>
                  📂 Accordéon dépliable
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => setHtmlContent(`<pre style="background:#1e293b;color:#e2e8f0;padding:1rem;border-radius:0.5rem;overflow-x:auto;"><code>$ votre commande ici</code></pre>`)}>
                  💻 Bloc de code terminal
                </Button>
              </TabsContent>
            </Tabs>
            <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
              <Button variant="outline" onClick={() => setHtmlDialogOpen(false)}>Annuler</Button>
              <Button onClick={insertFullHtmlBlock}>Insérer</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* PDF Import Button */}
        <div className="relative">
          <input
            type="file"
            accept=".pdf"
            onChange={handlePdfImport}
            className="hidden"
            id="pdf-import-input"
            disabled={pdfImporting}
          />
          <label
            htmlFor="pdf-import-input"
            className={`p-2 rounded hover:bg-muted transition-colors cursor-pointer flex items-center ${pdfImporting ? 'opacity-50 cursor-not-allowed' : 'text-muted-foreground'}`}
            title="Importer un PDF"
          >
            {pdfImporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileUp className="h-4 w-4" />
            )}
          </label>
        </div>
      </div>

      {/* Editor Content */}
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
